<script setup lang="ts">
import { TriangleAlert } from '@lucide/vue'
import { Button, Input, Label, ZhPanel, ZhSectionHeading } from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { type ApiTokenRow, api, formatAgo, type IssuedApiToken } from '@/api'
import CopyButton from '@/components/CopyButton.vue'

/**
 * #890: a token of the person's own, for a script, a scheduled job or a client that cannot sign in. It is the person
 * for their mailbox and nothing else: it reaches `/v1/mail` with what they chose here (read, or read and write) and is
 * refused every other route, an owner never sees it, and it stops the moment they revoke it or leave the enterprise.
 * Issued and revoked on Account, where their mail already is.
 */
const tokens = ref<ApiTokenRow[]>([])
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const name = ref('')
const level = ref<'read' | 'write'>('read')
const issued = ref<IssuedApiToken | null>(null)
const now = ref(Date.now())

async function load(): Promise<void> {
    try {
        tokens.value = await api.myTokens()
        error.value = null
    } catch {
        error.value = 'Could not load your tokens.'
    } finally {
        loading.value = false
    }
}

async function create(): Promise<void> {
    if (!name.value.trim() || busy.value) return
    busy.value = true
    error.value = null
    try {
        issued.value = await api.createMyToken({
            name: name.value.trim(),
            scopes: level.value === 'write' ? ['mail:read', 'mail:write'] : ['mail:read'],
        })
        name.value = ''
        await load()
    } catch (caught) {
        const message = caught instanceof Error ? caught.message : ''
        error.value = message.includes('api_token_name_invalid')
            ? 'Give the token a name of 1 to 80 characters.'
            : 'Could not create the token.'
    } finally {
        busy.value = false
    }
}

/** Revoke takes effect on the token's next request, so the list is reloaded rather than patched in place. */
async function revoke(row: ApiTokenRow): Promise<void> {
    if (!confirm(`Revoke "${row.name}"? Anything using it stops at its next request.`)) {
        return
    }
    busy.value = true
    error.value = null
    try {
        await api.revokeMyToken(row.id)
        await load()
    } catch {
        error.value = 'That did not work.'
    } finally {
        busy.value = false
    }
}

/** What the token may do, in the words the consent screen uses for the same scopes. */
function reach(row: ApiTokenRow): string {
    return row.scopes.includes('mail:write') ? 'Read and write' : 'Read'
}

onMounted(load)
</script>

<template>
	<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-my-tokens-heading" data-testid="my-tokens">
		<ZhSectionHeading id="account-my-tokens-heading">A token of your own</ZhSectionHeading>
		<p class="text-muted-foreground text-sm">
			For a script, a scheduled job or a client that cannot sign in. It is you, for your mail here, and nothing else:
			it reaches your mailbox with what you choose below and is refused everything else in the enterprise. Nobody but
			you sees it, and it stops the moment you revoke it or leave.
		</p>

		<!-- The one moment a secret is on screen, on a kit surface of its own so it cannot be read as part of the form. -->
		<ZhPanel v-if="issued" as="div" class="space-y-2" data-testid="issued-my-token">
			<p class="flex items-center gap-2 text-sm font-medium">
				<TriangleAlert class="size-4" />
				Copy this now — it is never shown again.
			</p>
			<div class="flex items-center gap-2">
				<code class="grow overflow-x-auto font-mono text-xs" data-testid="issued-my-token-secret">{{ issued.token }}</code>
				<CopyButton :value="issued.token" />
			</div>
			<Button size="sm" variant="outline" data-testid="dismiss-my-token" @click="issued = null">Done</Button>
		</ZhPanel>

		<form class="space-y-3 text-sm" data-testid="create-my-token" @submit.prevent="create">
			<div class="space-y-1">
				<Label for="my-token-name">Name</Label>
				<Input id="my-token-name" v-model="name" placeholder="Mail script on my laptop" maxlength="80" required :disabled="busy" />
			</div>
			<fieldset class="space-y-1">
				<legend class="text-sm font-medium">What it may do</legend>
				<label class="flex items-center gap-2">
					<input v-model="level" type="radio" value="read" data-testid="my-token-level-read" :disabled="busy" />
					<span><strong>Read</strong>: list and open your mail.</span>
				</label>
				<label class="flex items-center gap-2">
					<input v-model="level" type="radio" value="write" data-testid="my-token-level-write" :disabled="busy" />
					<span><strong>Read and write</strong>: also reply, send and file, as you, with no approval step.</span>
				</label>
			</fieldset>
			<Button type="submit" size="sm" :disabled="busy || !name.trim()">Create token</Button>
		</form>

		<p v-if="error" class="text-destructive text-sm" data-testid="my-tokens-error">{{ error }}</p>
		<p v-else-if="loading" class="text-muted-foreground text-sm">Loading…</p>
		<p v-else-if="tokens.length === 0" class="text-muted-foreground text-sm" data-testid="no-my-tokens">
			You have no token yet.
		</p>
		<ul v-else class="space-y-2 text-sm" data-testid="my-tokens-list">
			<li
				v-for="row in tokens"
				:key="row.id"
				class="flex flex-wrap items-center justify-between gap-2"
				:data-testid="`my-token-${row.id}`"
			>
				<span class="space-x-2">
					<span :class="row.revokedAt ? 'text-muted-foreground line-through' : 'font-medium'">{{ row.name }}</span>
					<code class="font-mono text-xs">…{{ row.hint }}</code>
					<span class="text-muted-foreground" data-reach>{{ reach(row) }}</span>
					<span class="text-muted-foreground text-xs">
						· {{ row.lastUsedAt ? `used ${formatAgo(now - Date.parse(row.lastUsedAt))}` : 'never used' }}
					</span>
				</span>
				<span v-if="row.revokedAt" class="text-muted-foreground text-xs" :data-testid="`my-token-revoked-${row.id}`">
					revoked
				</span>
				<Button
					v-else
					type="button"
					size="sm"
					variant="outline"
					:data-testid="`revoke-my-token-${row.id}`"
					:disabled="busy"
					@click="revoke(row)"
				>
					Revoke
				</Button>
			</li>
		</ul>
	</ZhPanel>
</template>
